import { useQuery } from "@apollo/client";
import Errors from "components/base/Errors";
import Spinner from "components/base/Spinner";
import PageV2 from "components/shared/PageV2";
import moment from "moment";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import ForecastProductView from "../ForecastProductView";
import { FETCH_SALES_FORECAST_SUBMISSION } from "./graphql";

function parseSubmissionData(value) {
  if (!value) return {};
  if (typeof value === "object") return value;
  if (typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function normalizeProductId(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^\d+$/.test(raw)) return String(parseInt(raw, 10));
  return raw;
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

const SubmissionDetailPage = () => {
  const { submissionId } = useParams();
  const { loading, error, data } = useQuery(FETCH_SALES_FORECAST_SUBMISSION, {
    variables: { submissionId },
  });

  const submission = data?.salesForecastSubmission;
  const parsed = useMemo(
    () => parseSubmissionData(submission?.data),
    [submission?.data],
  );
  const months = useMemo(
    () =>
      (Array.isArray(parsed?.columns) ? parsed.columns : [])
        .map((month) => String(month || "").trim())
        .filter(Boolean),
    [parsed],
  );
  const rows = useMemo(() => {
    const products = Array.isArray(submission?.products)
      ? submission.products
      : [];
    const productsById = products.reduce((acc, product) => {
      acc[normalizeProductId(product.id)] = product;
      return acc;
    }, {});
    const items = Array.isArray(parsed?.items) ? parsed.items : [];
    return items.map((item, index) => {
      const productId = normalizeProductId(item?.wis_product_id);
      return {
        key: `${productId || "unknown"}-${index}`,
        productId,
        product: productsById[productId],
        fcst: item?.fcst || {},
      };
    });
  }, [parsed, submission?.products]);

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  return (
    <PageV2
      title={`Forecast Submission #${submission.id}`}
      subtitle={`${submission.forecastCategory.name} | ${moment(
        submission.submittedAt,
      ).format(
        "YYYY-MM-DD HH:mm:ss",
      )} by ${submission.user?.email || "Unknown User"}`}
      backTo="./.."
    >
      <div className="h-full flex flex-col">
        <div className="card p-0 flex-1 overflow-auto text-xs">
          <table className="w-full">
            <thead className="sticky top-0 z-20 backdrop-blur bg-gray-200 bg-opacity-80 dark:bg-gray-800 dark:bg-opacity-80">
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="px-4 py-3 text-left whitespace-nowrap">
                  Product ({rows.length})
                </th>
                {months.map((month) => (
                  <th
                    key={month}
                    className="px-2 py-3 text-right whitespace-nowrap border-x border-gray-200 dark:border-gray-700"
                  >
                    {month}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={1 + months.length}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    No submission items.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.key}
                    className="border-y border-gray-100 dark:border-gray-700"
                  >
                    <td className="px-4 py-2">
                      {row.product ? (
                        <ForecastProductView product={row.product} />
                      ) : (
                        <span className="opacity-70">
                          Unknown product (ID: {row.productId || "N/A"})
                        </span>
                      )}
                    </td>
                    {months.map((month) => (
                      <td
                        key={`${row.key}-${month}`}
                        className="px-2 py-2 text-right whitespace-nowrap border-x border-gray-100 dark:border-gray-700"
                      >
                        {toNumber(row.fcst?.[month]).toLocaleString()}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </PageV2>
  );
};

export default SubmissionDetailPage;
